"use client";

import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { LayoutGrid, List, ListChecks, Play, PlayCircle } from "lucide-react";
import { cn } from "@/components/ui/cn";
import type { EventItem } from "@/lib/lecturehub/types";
import { useDict } from "@/components/i18n/DictProvider";
import type { Dict } from "@/lib/i18n";
import { isRecording, recordingSubtitle, recordingThumb, recordingTitle } from "./eventRecordings";

function itemLabels(t: Dict): Record<string, string> {
  return { lecture: t.learn.typeLecture, quiz: t.learn.typeQuiz };
}

export function EventProgrammeList({
  items,
  title,
  watchBase = null,
}: {
  items: EventItem[];
  title: string;
  /** /lang/library/events/[id]/[slug]: when set, recording rows link into the
   *  player. Null for guests / non-enrolled, where rows are display-only. */
  watchBase?: string | null;
}) {
  const t = useDict();
  const [view, setView] = useState<"list" | "grid">("list");

  return (
    <section className="course-section rounded-card p-6 md:p-8 bg-card border border-border/60">
      {/* Section title + view toggle on the same row. Toggle uses a tiny
          inset-rounded segmented control — same gold-accent active state as
          the rest of the editorial language. */}
      <header className="flex items-center justify-between gap-3 mb-5">
        <h2 className="!text-[20px] md:!text-[22px] !font-semibold !leading-tight !text-foreground !mb-0">
          {title}
        </h2>
        <div
          role="group"
          aria-label={t.listing.viewMode}
          className="inline-flex items-center gap-1 p-1 rounded-full border border-border/60"
        >
          <ViewBtn
            active={view === "list"}
            onClick={() => setView("list")}
            label={t.listing.listView}
          >
            <List className="size-3.5" aria-hidden />
          </ViewBtn>
          <ViewBtn
            active={view === "grid"}
            onClick={() => setView("grid")}
            label={t.listing.gridView}
          >
            <LayoutGrid className="size-3.5" aria-hidden />
          </ViewBtn>
        </div>
      </header>

      {view === "list" ? (
        <ListLayout items={items} watchBase={watchBase} />
      ) : (
        <GridLayout items={items} watchBase={watchBase} />
      )}
    </section>
  );
}

function ViewBtn({
  active,
  onClick,
  label,
  children,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      aria-label={label}
      title={label}
      className={cn(
        "size-7 inline-flex items-center justify-center rounded-full transition-colors",
        active
          ? "bg-accent text-accent-foreground"
          : "text-foreground/55 hover:bg-foreground/[0.04]",
      )}
    >
      {children}
    </button>
  );
}

/** Link target for a row, or null when it isn't a watchable recording / the
 *  viewer can't watch (guest, non-enrolled, public page). */
function watchHrefFor(it: EventItem, watchBase: string | null): string | null {
  return watchBase && isRecording(it) ? `${watchBase}/${it.id}` : null;
}

function ListLayout({ items, watchBase }: { items: EventItem[]; watchBase: string | null }) {
  const t = useDict();
  return (
    <ul className="divide-y divide-border/60 -my-2">
      {items.map((it, i) => {
        const Icon = it.refType === "lecture" ? PlayCircle : ListChecks;
        const title = recordingTitle(it);
        const typeLabel = itemLabels(t)[it.refType] ?? it.refType;
        const thumb = recordingThumb(it);
        const subtitle = recordingSubtitle(it);
        const href = watchHrefFor(it, watchBase);

        const inner = (
          <>
            <span className="text-foreground/40 text-[11px] tabular-nums font-display font-extrabold tracking-[0.12em] w-7 text-right">
              {String(i + 1).padStart(2, "0")}
            </span>
            <div className="relative w-32 md:w-40 aspect-[16/9] rounded-md overflow-hidden bg-muted shrink-0">
              {thumb ? (
                <Image
                  src={thumb.uri}
                  alt={thumb.alt}
                  fill
                  sizes="(min-width: 768px) 160px, 128px"
                  className="object-cover"
                />
              ) : (
                <span aria-hidden className="absolute inset-0 grid place-items-center text-accent">
                  <Icon className="size-4" />
                </span>
              )}
              {href ? (
                <span
                  aria-hidden
                  className="absolute inset-0 grid place-items-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Play className="size-5 fill-current text-white" />
                </span>
              ) : null}
            </div>
            <div className="min-w-0">
              <p className="font-display font-semibold text-[14px] md:text-[15px] text-foreground leading-snug line-clamp-2 group-hover:text-accent">
                {title}
              </p>
              {subtitle ? (
                <p className="mt-0.5 text-[12px] text-muted-foreground truncate">{subtitle}</p>
              ) : null}
            </div>
            <span className="text-[10px] uppercase tracking-[0.14em] font-bold text-foreground/50 shrink-0">
              {typeLabel}
            </span>
          </>
        );

        const rowClass = "grid grid-cols-[auto_auto_1fr_auto] items-center gap-3 md:gap-4 py-3.5";

        return (
          <li key={it.id}>
            {href ? (
              <Link href={href} className={cn("group", rowClass, "transition-colors")}>
                {inner}
              </Link>
            ) : (
              <div className={rowClass}>{inner}</div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function GridLayout({ items, watchBase }: { items: EventItem[]; watchBase: string | null }) {
  const t = useDict();
  return (
    <ul className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
      {items.map((it, i) => {
        const Icon = it.refType === "lecture" ? PlayCircle : ListChecks;
        const title = recordingTitle(it);
        const typeLabel = itemLabels(t)[it.refType] ?? it.refType;
        const thumb = recordingThumb(it);
        const subtitle = recordingSubtitle(it);
        const href = watchHrefFor(it, watchBase);

        const inner = (
          <>
            <div className="relative aspect-[16/9] bg-muted">
              {thumb ? (
                <Image
                  src={thumb.uri}
                  alt={thumb.alt}
                  fill
                  sizes="(min-width: 1280px) 33vw, (min-width: 640px) 50vw, 100vw"
                  className="object-cover"
                />
              ) : (
                <span aria-hidden className="absolute inset-0 grid place-items-center text-accent">
                  <Icon className="size-6" />
                </span>
              )}
              {href ? (
                <span
                  aria-hidden
                  className="absolute inset-0 grid place-items-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Play className="size-7 fill-current text-white" />
                </span>
              ) : null}
              <span className="absolute top-2 left-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.14em] font-bold text-foreground bg-card/85 backdrop-blur ring-1 ring-inset ring-foreground/10">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="absolute top-2 right-2 inline-flex items-center px-2 py-0.5 rounded-full text-[9.5px] uppercase tracking-[0.14em] font-bold text-foreground bg-card/85 backdrop-blur ring-1 ring-inset ring-foreground/10">
                {typeLabel}
              </span>
            </div>
            <div className="p-4 flex-1 flex flex-col">
              <p className="font-display font-semibold text-[15px] text-foreground leading-snug line-clamp-2 group-hover:text-accent">
                {title}
              </p>
              {subtitle ? (
                <p className="mt-1.5 text-[12.5px] text-muted-foreground line-clamp-1">
                  {subtitle}
                </p>
              ) : null}
            </div>
          </>
        );

        const cardClass = "rounded-card border border-border/60 overflow-hidden flex flex-col";

        return (
          <li key={it.id}>
            {href ? (
              <Link href={href} className={cn("group block h-full", cardClass)}>
                {inner}
              </Link>
            ) : (
              <div className={cardClass}>{inner}</div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
